import { Button, Modal } from "@legal-500/legal500-design-system";
import { useEffect, useState } from "react";
import {
  intelligenceSyncerService,
  isIntelligenceSyncerConflictError,
  extractConflictingRunId,
  type IntelligenceSyncerJobType,
} from "../../../api/intelligenceSyncer.service.js";
import styles from "./IntelligenceSyncer.module.css";

type TriggerIntelligenceSyncModalProps = {
  isOpen: boolean;
  jobType: IntelligenceSyncerJobType;
  onClose: () => void;
  onTriggered: (runId: string) => void;
  onConflict: (existingRunId: string | null) => void;
};

const JOB_TYPE_TITLES: Record<IntelligenceSyncerJobType, string> = {
  "bulk-load": "Trigger Bulk Load",
  incremental: "Trigger Incremental Sync",
};

export function TriggerIntelligenceSyncModal({
  isOpen,
  jobType,
  onClose,
  onTriggered,
  onConflict,
}: TriggerIntelligenceSyncModalProps) {
  const [loadAll, setLoadAll] = useState(true);
  const [retainPreviousKeyspace, setRetainPreviousKeyspace] = useState(false);
  const [writeV2Layout, setWriteV2Layout] = useState(true);
  const [maxArticles, setMaxArticles] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setLoadAll(true);
    setRetainPreviousKeyspace(false);
    setWriteV2Layout(true);
    setMaxArticles("");
    setSubmitting(false);
    setError(null);
  }, [isOpen]);

  // The live article count drifts day to day, so there's no stable number that means
  // "everything" — omitting maxArticles entirely (rather than hardcoding a large cap) is
  // what tells the syncer to run unbounded, and stays correct as the count moves.
  const parsedMaxArticles =
    jobType === "bulk-load" && !loadAll && maxArticles.trim() !== ""
      ? Number(maxArticles)
      : undefined;

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const response = await intelligenceSyncerService.trigger({
        jobType,
        maxArticles: parsedMaxArticles,
        ...(jobType === "bulk-load" && {
          retainPreviousKeyspace,
          keyspaceLayout: writeV2Layout ? "v2" : "v1",
        }),
      });
      onTriggered(response.runId);
    } catch (err) {
      if (isIntelligenceSyncerConflictError(err)) {
        onConflict(extractConflictingRunId(err));
        return;
      }
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={JOB_TYPE_TITLES[jobType]}
      size="small"
      blurBackground
    >
      <div className={styles.modalBody}>
        {error && <div className={styles.errorBanner}>{error}</div>}

        {jobType === "bulk-load" ? (
          <>
            <p>
              Runs a full re-crawl of intelligence articles. Load everything, or cap the number of
              articles processed for a bounded test run.
            </p>
            <div className={styles.formField}>
              <label className={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={loadAll}
                  onChange={event => {
                    setLoadAll(event.target.checked);
                    if (event.target.checked) setMaxArticles("");
                  }}
                />
                <span>
                  <strong>Load all</strong> — run unbounded against the full live article set,
                  ignoring the cap below.
                </span>
              </label>
            </div>
            <div className={styles.formField}>
              <label htmlFor="maxArticles">Max articles (optional)</label>
              <input
                id="maxArticles"
                type="number"
                min={1}
                step={1}
                value={maxArticles}
                disabled={loadAll}
                onChange={event => setMaxArticles(event.target.value)}
              />
            </div>
            <div className={styles.formField}>
              <label className={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={retainPreviousKeyspace}
                  onChange={event => setRetainPreviousKeyspace(event.target.checked)}
                />
                <span>
                  <strong>Keep the previous catalog</strong>
                  <br />
                  Keeps the current catalog in Redis (about 30 GB) as a fallback. Discard it from DB
                  Manager once the new catalog is verified. Leave unticked to delete it
                  automatically.
                </span>
              </label>
            </div>
            <div className={styles.formField}>
              <label className={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={writeV2Layout}
                  onChange={event => setWriteV2Layout(event.target.checked)}
                />
                <span>
                  <strong>Write the v2 layout</strong>
                  <br />
                  Spreads article docs across all Redis shards. Untick only to write the old
                  single-shard v1 layout.
                </span>
              </label>
            </div>
          </>
        ) : (
          <p>
            Runs a delta sync, picking up only articles that have changed since the last successful
            run.
          </p>
        )}

        <div className={styles.modalActions}>
          <Button color="default" variant="outlined" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Triggering…" : "Trigger"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
